import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  STAFF_BILLING_OTHER_COUPON_CATALOG,
  STAFF_BILLING_OTHER_COUPON_PREVIEW,
  STAFF_BILLING_APPLY_OTHER_COUPON,
  STAFF_BILLING_REMOVE_OTHER_COUPON,
} from '@staff-app/graphql-client';
import type { BillingCustomerDiscount } from './useBillingConsole';

/**
 * OptiHub → Early Promoters Coupon (ROOT only) — the read + write hooks for the second coupon
 * surface, over `public.billing_other_coupons` (migration 040).
 *
 * Deliberately NOT added to `useBillingConsole.ts`: that file serves the Billing Console's
 * eight cards, and the whole point of this feature is that the two lists never meet. Sharing
 * a hooks module is how a "quick reuse" later turns into one picker reading the other's
 * catalog.
 *
 * `BillingCustomerDiscount` IS shared — it is Stripe's shape, not either list's.
 */

/** One entry of the Early Promoters Coupon picker. */
export type OtherCouponOption = {
  /** List key, e.g. `EARLY-PROMOTERS-20`. The ONLY coupon handle the card sends back. */
  couponKey: string;
  label: string;
  /** Human terms — what the customer was actually promised. Free text on the row. */
  terms?: string | null;
  /** Stripe's own name for the coupon, cached at save time. Display only, never money. */
  discountSummary?: string | null;
  /** Shown so an operator can confirm the row against Stripe. Never sent back. */
  stripeCouponId: string;
};

export type OtherCouponCatalog = {
  coupons: OtherCouponOption[];
  /**
   * True = the TABLE is missing in this environment (migration 040 never applied), which is
   * a different message and a different fix from an environment where nobody has added a row
   * yet. Branch on it before saying "no coupons".
   */
  couponsUnavailable: boolean;
};

/**
 * What the customer panel renders.
 *
 * Leaner than `BillingCouponPreview`: no certificates, because this path reads none. The
 * three `flag*` fields are what Stripe holds TODAY — they drive the read-only flag line, and
 * never the checkboxes, which are add-only and always start unticked.
 */
export type OtherCouponPreview = {
  stripeId: string;
  /** The account the SERVER resolved from `payment.stripe_id` — never client-supplied. */
  accountId: string;
  companyName?: string | null;
  stripeName?: string | null;
  /**
   * Non-null does NOT refuse the apply here, unlike on the Billing Console card — it is what
   * makes the form demand `replaceExisting`.
   */
  currentDiscount?: BillingCustomerDiscount | null;
  taxStatus?: string | null;
  /** ⚠ `early_promotors` really is spelled that way in Stripe. Do not "fix" it. */
  flagEarlyPromotors: boolean;
  flagReseller: boolean;
  /** `early_promoters_granted_by` — who set a flag last, if anyone. */
  flagGrantedBy?: string | null;
};

/**
 * What an apply and a remove BOTH answer: what discount does this customer have now, and
 * where do I check it. The apply adds `flagsApplied` on top; a removal writes no flags.
 */
export type OtherCouponOutcome = {
  stripeId: string;
  accountId: string;
  companyName?: string | null;
  /** The discount now on the customer, read BACK from Stripe. Null after a remove. */
  discount?: BillingCustomerDiscount | null;
  /**
   * The coupon that is no longer there — displaced by an apply, or taken off by a remove.
   * Stripe keeps no history of it, so with the audit row this is the only record it existed.
   */
  previousCouponId?: string | null;
  taxStatus?: string | null;
  /**
   * True when this request ALSO changed the Tax status: a removal (or displacement) of the
   * coupon that was the REASON for an exemption puts the customer back to Taxable.
   */
  taxStatusChanged: boolean;
  customerUrl: string;
  message: string;
};

export type OtherCouponResult = OtherCouponOutcome & {
  /** The metadata keys that LANDED, e.g. `['reseller', 'early_promoters_granted_by']`. */
  flagsApplied: string[];
};

export type ApplyOtherCouponVars = {
  input: {
    stripeId: string;
    couponKey: string;
    comment: string;
    /** Only meaningful when a DIFFERENT discount is already on the customer. */
    replaceExisting?: boolean;
    /** Add-only: true writes the flag, false/absent leaves whatever is there. */
    flagEarlyPromotors?: boolean;
    flagReseller?: boolean;
  };
};

export function useOtherCouponCatalog() {
  return useQuery<{ staffBillingOtherCouponCatalog: OtherCouponCatalog }>(
    STAFF_BILLING_OTHER_COUPON_CATALOG,
    { fetchPolicy: 'cache-and-network' },
  );
}

/**
 * Look up the customer the coupon would be applied to. LAZY on purpose: fired when the
 * operator finishes typing a `cus_` id, not on mount. `network-only` is not tidiness — a
 * cached preview could show "no discount" for a customer who has one since, and here that
 * would mean the Replace checkbox never appears and the apply is refused by the server for a
 * reason the card never showed.
 */
export function useOtherCouponPreview() {
  return useLazyQuery<
    { staffBillingOtherCouponPreview: OtherCouponPreview },
    { stripeId: string }
  >(STAFF_BILLING_OTHER_COUPON_PREVIEW, { fetchPolicy: 'network-only' });
}

export function useApplyOtherCoupon() {
  return useMutation<
    { staffBillingApplyOtherCoupon: OtherCouponResult },
    ApplyOtherCouponVars
  >(STAFF_BILLING_APPLY_OTHER_COUPON);
}

/**
 * Take the customer's current discount off.
 *
 * No flags, no coupon key and no required comment — a removal classifies nobody and names
 * nothing; the server reads back off Stripe what it is about to remove. The comment is
 * mandatory on the Billing Console's equivalent and optional here: the rule is per-surface,
 * and one typed anyway is still recorded.
 *
 * Its own op rather than the Billing Console's `staffBillingRemoveCoupon`, so the Activity Log
 * files the row under the tab it was pressed on. Same service method underneath, which is what
 * keeps the tax-status handling identical.
 */
export function useRemoveOtherCoupon() {
  return useMutation<
    { staffBillingRemoveOtherCoupon: OtherCouponOutcome },
    { stripeId: string; comment?: string }
  >(STAFF_BILLING_REMOVE_OTHER_COUPON);
}
